import React from 'react';
import styled from 'styled-components';

/**
 * "Sign-in didn't finish" toast for the lost-redirect case (Bug 2): a
 * mobile OAuth redirect marker was present on return but getRedirectResult
 * delivered no credential (storage partitioning, ITP, user hit Back).
 * Until now that state fired `auth_redirect_lost` telemetry and nothing
 * else — the user was silently left anonymous. Rendered by
 * AuthGateProvider when `redirectFailed` is true.
 *
 * Minimal text per the ICP (people don't read): one short line, one
 * action, one dismiss. role="alert" so assistive tech announces it.
 */

const Toast = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 80;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  max-width: calc(100vw - 32px);
  padding: 12px 14px 12px 18px;
  background: #ffffff;
  border: 1px solid ${({ theme }) => theme.color.warmBorder};
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14);
  font-family: ${({ theme }) => theme.font.sans};
`;

const Message = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: hsl(var(--foreground));
  white-space: nowrap;
`;

const RetryButton = styled.button`
  border: 0;
  border-radius: 9999px;
  padding: 8px 14px;
  background: ${({ theme }) => theme.color.clay};
  color: #ffffff;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    opacity: 0.9;
  }
`;

const DismissButton = styled.button`
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.color.mutedText};
  font-size: 18px;
  line-height: 1;
  padding: 6px;
  cursor: pointer;
  border-radius: 9999px;
  &:hover {
    background: ${({ theme }) => theme.color.creamLight};
  }
`;

const DiagLine = styled.div`
  flex-basis: 100%;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 11px;
  line-height: 1.4;
  color: ${({ theme }) => theme.color.mutedText};
  word-break: break-all;
  user-select: all;
`;

export interface RedirectFailedToastProps {
  /** Re-open the sign-in flow (AuthGate wires this to requestSignIn). */
  onRetry: () => void;
  /** Acknowledge and hide the toast. */
  onDismiss: () => void;
  /**
   * TEMPORARY DIAGNOSTIC (mobile redirect sign-in loss, 2026-07-13): compact
   * on-screen readout of the redirect-return state — the first-party sink
   * rejects `auth_redirect_lost` (not allowlisted) and device Web Inspector
   * isn't available, so the screen is the transport. Remove together with
   * the AuthGateContext/firebaseConfig diagnostics once the bug is closed.
   */
  diagnostics?: string;
}

export const RedirectFailedToast: React.FC<RedirectFailedToastProps> = ({
  onRetry,
  onDismiss,
  diagnostics,
}) => (
  <Toast role="alert" data-testid="redirect-failed-toast">
    <Message>Sign-in didn&rsquo;t finish</Message>
    <RetryButton type="button" onClick={onRetry}>
      Try again
    </RetryButton>
    <DismissButton type="button" aria-label="Dismiss" onClick={onDismiss}>
      ×
    </DismissButton>
    {diagnostics ? (
      <DiagLine data-testid="redirect-failed-diagnostics">{diagnostics}</DiagLine>
    ) : null}
  </Toast>
);
